import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, Users } from "lucide-react";
import { api } from "../lib/api";

/**
 * Admin demand widget: what's currently sitting in logged-in customers' carts.
 * Hidden when no carts exist. Anonymous shoppers are NOT tracked.
 */
export default function LiveCartsWidget() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api.get("/admin/live-carts")
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (rows.length === 0) return null;

  const totalUnits = rows.reduce((s, r) => s + r.total_quantity, 0);
  const visible = expanded ? rows : rows.slice(0, 4);

  return (
    <div
      data-testid="live-carts-widget"
      className="mt-4 bg-[#2eb6a8]/8 border-2 border-[#2eb6a8]/40 rounded-2xl p-5"
    >
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <ShoppingCart size={16} className="text-[#1f8d83]"/>
          <div>
            <div className="font-display font-extrabold text-[#1c1815] text-base">Live Carts</div>
            <div className="text-[11px] text-[#1f8d83] font-bold uppercase tracking-[0.18em]">
              <Users size={10} className="inline align-text-bottom mr-1"/>
              {totalUnits} unit{totalUnits === 1 ? "" : "s"} · {rows.length} item{rows.length === 1 ? "" : "s"} in shopper carts
            </div>
          </div>
        </div>
      </div>
      <div className="space-y-2">
        {visible.map((r) => (
          <Link
            key={r.product_id}
            to={`/product/${r.slug}`}
            target="_blank"
            data-testid={`live-cart-row-${r.product_id}`}
            className="flex items-center gap-3 p-2.5 bg-white border border-[#e5dccd] rounded-xl hover:border-[#2eb6a8] hover:shadow-sm transition-all"
          >
            <img src={r.image_url} alt="" className="w-12 h-12 object-cover rounded-lg flex-shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-[#1c1815] truncate text-sm" title={r.title}>{r.title}</div>
              <div className="mt-0.5 text-[11px] text-[#8a8076]">
                ${(r.price || 0).toFixed(2)} · {r.shopper_count} shopper{r.shopper_count === 1 ? "" : "s"}
              </div>
            </div>
            <div className="flex-shrink-0 text-right">
              <div className="font-display font-extrabold text-2xl text-[#1f8d83] leading-none">{r.total_quantity}</div>
              <div className="text-[9px] uppercase tracking-[0.18em] text-[#8a8076] font-bold mt-0.5">in carts</div>
            </div>
          </Link>
        ))}
      </div>
      {rows.length > 4 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="live-carts-toggle"
          className="mt-3 text-[11px] font-bold uppercase tracking-[0.18em] text-[#1f8d83] hover:text-[#176862]"
        >
          {expanded ? "Show less" : `Show ${rows.length - 4} more`}
        </button>
      )}
    </div>
  );
}
